import { useEffect, useRef, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

// Flag per session: il toast appare UNA volta per sessione browser (sessionStorage).
// Evitiamo di mostrarlo ad ogni apertura di chat — sarebbe rumoroso.
const TOAST_FLAG_KEY = 'qp.memory.toast.shown';

/**
 * MemoryChip — chip visibile in cima alla chat che indica all'utente
 * quante conversazioni precedenti il coach ha in memoria per questa sessione.
 *
 * Comportamento:
 *  - Fetch best-effort di `/api/coach/log/memory` al mount (silenzioso, no toast).
 *  - Se `sessions === 0` (o errore) → non renderizza nulla (chip invisibile).
 *  - Click sul chip → expand inline mostrando il blocco testo `memory_text` in
 *    formato monospace dentro un riquadro scrollabile.
 *  - Click su X → chiude l'expand.
 *
 * Trasparenza: l'utente capisce sempre cosa "sa" il coach senza dover
 * sbirciare nei log. Implementato lato frontend, no impatto sul backend.
 */
export default function MemoryChip() {
  const [data, setData] = useState(null);     // {memory_text, sessions} | null
  const [expanded, setExpanded] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    api
      .get('/coach/log/memory?limit=6', { silent: true })
      .then((r) => {
        const payload = r.data || null;
        setData(payload);
        // Mini-toast "il coach mi conosce" — solo se ci sono conversazioni
        // E solo la prima volta in questa sessione browser.
        if (payload && payload.sessions > 0) {
          let alreadyShown = false;
          try { alreadyShown = sessionStorage.getItem(TOAST_FLAG_KEY) === '1'; }
          catch { /* sessionStorage non disponibile: mostra comunque */ }
          if (!alreadyShown) {
            const n = payload.sessions;
            toast(`Sto leggendo le tue ultime ${n} conversazioni…`, {
              icon: <Sparkles size={14} strokeWidth={1.5} />,
              duration: 2500,
              description: 'Il coach ha contesto delle tue sessioni precedenti.',
            });
            try { sessionStorage.setItem(TOAST_FLAG_KEY, '1'); } catch { /* ignore */ }
          }
        }
      })
      .catch(() => setData(null));
  }, []);

  // Nasconde finché non sappiamo il count, o se 0
  if (!data || !data.sessions) return null;

  return (
    <div className="mb-3" data-testid="coach-memory-chip-wrapper">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2 py-1 border border-line bg-canvas hover:bg-linesoft hover:border-ink transition-colors text-[10px] font-mono uppercase tracking-[0.15em] text-soft"
        title={expanded ? 'Nascondi memoria' : 'Vedi le conversazioni che il coach sta usando'}
        aria-expanded={expanded}
        data-testid="coach-memory-chip"
      >
        <Sparkles size={11} strokeWidth={1.5} />
        <span>{data.sessions} {data.sessions === 1 ? 'conversazione' : 'conversazioni'} nel contesto</span>
      </button>

      {expanded && (
        <div
          className="mt-2 border border-line bg-canvas p-3 relative"
          data-testid="coach-memory-chip-expanded"
        >
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="absolute top-1 right-1 text-mute hover:text-ink p-1"
            aria-label="Chiudi"
            data-testid="coach-memory-chip-close"
          >
            <X size={12} strokeWidth={1.5} />
          </button>
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mute mb-2">
            Memoria iniettata nel prompt del coach
          </p>
          <pre className="font-mono text-[11px] text-soft leading-relaxed whitespace-pre-wrap max-h-[280px] overflow-y-auto pr-4">
            {data.memory_text || '(memoria vuota)'}
          </pre>
          <p className="font-mono text-[9px] text-mute mt-2 italic">
            Il coach usa questa memoria solo come contesto — non duplica suggerimenti già applicati.
          </p>
        </div>
      )}
    </div>
  );
}
